'use client';

import React, { useState, useEffect } from 'react';
import { History, GitCompare, Trash2, TrendingUp, Search, SlidersHorizontal, X } from 'lucide-react';
import BacktestRunCard from './BacktestRunCard';
import BacktestComparePanel from './BacktestComparePanel';
import BacktestRunDetailModal from './BacktestRunDetailModal';
import { BacktestRun, loadBacktestHistoryAsync, deleteBacktestRunAsync } from '../utils/backtestStorage';

export default function BacktestHistoryDashboard() {
  const [runs, setRuns] = useState<BacktestRun[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [compareMode, setCompareMode] = useState(false);
  const [detailRun, setDetailRun] = useState<BacktestRun | null>(null);
  const [search, setSearch] = useState('');
  const [filterSymbol, setFilterSymbol] = useState('All');

  useEffect(() => {
    loadBacktestHistoryAsync().then((data) => setRuns(data));
  }, []);

  const filtered = runs.filter((r) => {
    const matchSearch =
      search === '' ||
      r.name.toLowerCase().includes(search.toLowerCase()) ||
      r.params.symbol.toLowerCase().includes(search.toLowerCase());
    const matchSymbol = filterSymbol === 'All' || r.params.symbol === filterSymbol;
    return matchSearch && matchSymbol;
  });

  const symbols = ['All', ...Array.from(new Set(runs.map((r) => r.params.symbol)))];

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev.length < 3 ? [...prev, id] : prev
    );
  };

  const deleteRun = async (id: string) => {
    const updated = await deleteBacktestRunAsync(id);
    setRuns(updated);
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  };

  const clearAll = async () => {
    const ids = runs.map((r) => r.id);
    let remaining = runs;
    for (const id of ids) {
      remaining = await deleteBacktestRunAsync(id);
    }
    setRuns([]);
    setSelectedIds([]);
  };

  const selectedRuns = runs.filter((r) => selectedIds.includes(r.id));

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <History size={16} className="text-primary" />
            </div>
            <h1 className="text-xl font-bold text-foreground">Backtest History</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            {runs.length} saved run{runs.length !== 1 ? 's' : ''} · Click cards to view details, select up to 3 to compare
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selectedIds.length >= 2 && (
            <button
              onClick={() => setCompareMode(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-background text-sm font-600 hover:bg-primary/90 transition-colors"
            >
              <GitCompare size={15} />
              Compare {selectedIds.length} Runs
            </button>
          )}
          {runs.length > 0 && (
            <button
              onClick={clearAll}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-muted-foreground hover:text-negative hover:border-negative/30 text-sm font-500 transition-colors"
            >
              <Trash2 size={14} />
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      {runs.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name or symbol…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-input border border-border rounded-lg pl-9 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/60"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X size={12} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <SlidersHorizontal size={13} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Symbol:</span>
            {symbols.map((sym) => (
              <button
                key={`sym-filter-${sym}`}
                onClick={() => setFilterSymbol(sym)}
                className={`px-2.5 py-1 rounded-lg text-xs font-500 transition-colors ${
                  filterSymbol === sym
                    ? 'bg-primary/10 text-primary border border-primary/30' :'text-muted-foreground hover:text-foreground border border-transparent hover:border-border'
                }`}
              >
                {sym}
              </button>
            ))}
          </div>
          {selectedIds.length > 0 && (
            <button
              onClick={() => setSelectedIds([])}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors ml-auto"
            >
              Clear selection ({selectedIds.length})
            </button>
          )}
        </div>
      )}

      {/* Content */}
      {runs.length === 0 ? (
        <EmptyState />
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Search size={32} className="text-muted-foreground mb-3 opacity-40" />
          <p className="text-sm text-muted-foreground">No runs match your search</p>
          <button onClick={() => { setSearch(''); setFilterSymbol('All'); }} className="text-xs text-primary mt-2 hover:underline">
            Clear filters
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((run) => (
            <BacktestRunCard
              key={run.id}
              run={run}
              selected={selectedIds.includes(run.id)}
              onSelect={() => toggleSelect(run.id)}
              onView={() => setDetailRun(run)}
              onDelete={() => deleteRun(run.id)}
              selectionDisabled={selectedIds.length >= 3 && !selectedIds.includes(run.id)}
            />
          ))}
        </div>
      )}

      {/* Compare panel */}
      {compareMode && selectedRuns.length >= 2 && (
        <BacktestComparePanel runs={selectedRuns} onClose={() => setCompareMode(false)} />
      )}

      {/* Detail modal */}
      {detailRun && (
        <BacktestRunDetailModal run={detailRun} onClose={() => setDetailRun(null)} />
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
        <History size={28} className="text-muted-foreground opacity-50" />
      </div>
      <h3 className="text-base font-600 text-foreground mb-2">No backtest runs saved yet</h3>
      <p className="text-sm text-muted-foreground max-w-xs">
        Run a backtest in the Auto-Backtest Engine and click <strong className="text-foreground">Save Run</strong> to store it here for future review and comparison.
      </p>
      <a
        href="/auto-backtest-engine"
        className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 text-primary border border-primary/20 text-sm font-500 hover:bg-primary/20 transition-colors"
      >
        <TrendingUp size={15} />
        Go to Auto-Backtest Engine
      </a>
    </div>
  );
}
